import axios, { type AxiosError } from 'axios';
import { settings } from '../../settings';

const SLOW_NETWORK_THRESHOLD = 600;

const checkIfCertificateError = (response: AxiosError): boolean => {
  return (
    response?.code === 'CERT_HAS_EXPIRED' ||
    response?.code === 'DEPTH_ZERO_SELF_SIGNED_CERT' ||
    response?.message?.includes('certificate') ||
    response?.message?.includes('SSL')
  );
};

const checkApis = async (isRenderer = false): Promise<string> => {
  const error = `Network connection error ${isRenderer ? 'renderer' : 'main'}: `;
  try {
    const {
      WEB_APP,
      OKTA_ISSUER,
      API_DOMAIN,
      EDITOR_BASE_URL,
      RECORDER_URL,
      EDITOR_PLAYER_PATH,
      EDITOR_RUNTIME_PATH,
      EDITOR_BANNER_PATH,
      ANALYTICS_DEMOSTACK_API,
      FILES_SERVICE_URL,
      RECORDER_JS_BASE_URL,
      DS_RECORDER_JS_PATH,
    } = settings;
    const domains = [
      'https://www.google.com/',
      WEB_APP,
      OKTA_ISSUER.replace('/oauth2/default', ''),
      API_DOMAIN.concat('/healthcheck'),
      EDITOR_BASE_URL.concat(EDITOR_PLAYER_PATH),
      EDITOR_BASE_URL.concat(EDITOR_RUNTIME_PATH),
      EDITOR_BASE_URL.concat(EDITOR_BANNER_PATH),
      RECORDER_URL.concat('/healthcheck'),
      RECORDER_JS_BASE_URL.concat(DS_RECORDER_JS_PATH),
      ANALYTICS_DEMOSTACK_API.concat('/healthz'),
      FILES_SERVICE_URL.concat('/healthcheck'),
    ];
    const requests = domains.map((domain) =>
      axios.get(`${domain}`).then((res) => res.data)
    );
    const res = await Promise.allSettled(requests);
    const errors = res.reduce((accumulator: any, currentValue: any, index) => {
      const url = domains[index];
      let msg = `${error}${url}`;
      if (currentValue?.status === 'rejected') {
        if (checkIfCertificateError(currentValue?.reason))
          msg = `${msg}: Certificate error`;
        return [...accumulator, msg];
      }
      if (
        currentValue?.status === 'fulfilled' &&
        currentValue?.value?.status &&
        currentValue.value.status.toLowerCase() !== 'ok'
      ) {
        return [...accumulator, `${msg} ${currentValue?.value?.status}`];
      }
      return accumulator;
    }, []);

    return errors.length ? errors.join('\n') : '';
  } catch (err) {
    const msg = error.concat('error while checking APIs');
    console.error(msg, err);
    return msg;
  }
};

const measureNetworkPerformance = async (
  url = settings.WEB_APP
): Promise<string> => {
  const error = 'Slow internet performance: ';
  const startTime = performance.now();
  try {
    await fetch(url);
    const endTime = performance.now();
    const timeTaken = endTime - startTime;
    const slow = timeTaken > SLOW_NETWORK_THRESHOLD;
    return slow ? error.concat('check you your internet connection') : '';
  } catch (err) {
    console.error(err);
    return error.concat('error on network performance check');
  }
};

export { checkApis, measureNetworkPerformance };
